import { PRODUCT_ID_DATA_ONLY } from '@constants/constants';
import { Order, OrderResultFinding, ResultsType } from '@customTypes/order';
import { Requisition } from '@customTypes/requisition';
import { getOrderTestDisplayLabels } from '@helpers/patient';
import { getOrderResult, isOrderReported, RESULT_TYPE_CONFIG } from '@helpers/order';
import { isMedicationResponseOrder, isRareDiseaseProduct } from '@helpers/product';

export const PGX_SUMMARY_VALUE = 'See details';

const pgxFindings = RESULT_TYPE_CONFIG[ResultsType.NO_DATA_AVAILABLE].findings;

export type PatientResultsSummaryGroup = 'panel' | 'polygenic' | 'medication' | 'rareDisease';

const RISK_FINDINGS = new Set<string>([
  OrderResultFinding.AVERAGE_RISK,
  OrderResultFinding.BORDERLINE_RISK,
  OrderResultFinding.ELEVATED_RISK,
  OrderResultFinding.HIGH_RISK,
  OrderResultFinding.INTERMEDIATE_RISK,
  OrderResultFinding.LOW_RISK,
  OrderResultFinding.INCREASED_RISK,
  OrderResultFinding.NOT_INCREASED_RISK,
  OrderResultFinding.DECREASED_RISK,
  OrderResultFinding.NOT_ANALYZED,
]);

const RELATIVE_RISK_PATTERN = /this patient's relative risk is .+?[a-z]\./i;

export interface PatientResultsSummaryRow {
  orderId: string;
  testHeadline: string;
  testSubline?: string;
  summaryText: string;
  glanceDetail: string | null;
  resultType: ResultsType;
  finding: string | null;
  group: PatientResultsSummaryGroup;
  isMedicationResponse: boolean;
  reportedAt?: string;
}

export const isPgxSummaryOrder = (order: Order): boolean => {
  if (isMedicationResponseOrder(order)) {
    return true;
  }

  const results = order.results ?? [];
  if (!results.length) {
    return false;
  }

  return results.every(result => pgxFindings.includes(result.finding));
};

const getOrderSummaryText = (order: Order): string | null => {
  if (isPgxSummaryOrder(order)) {
    return PGX_SUMMARY_VALUE;
  }

  const texts = (order.results ?? []).map(result => result.text.trim()).filter(Boolean);
  if (!texts.length) {
    return null;
  }

  return texts.join(' ');
};

const orderHasSummaryData = (order: Order): boolean => getOrderSummaryText(order) !== null;

const relativeRiskGlance = (order: Order): string | null => {
  for (const result of order.results ?? []) {
    const match = result.details?.match(RELATIVE_RISK_PATTERN);
    if (!match) {
      continue;
    }

    const sentence = match[0];
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  return null;
};

const glanceDetail = (order: Order, group: PatientResultsSummaryGroup, summaryText: string): string | null => {
  if (group === 'medication') {
    return summaryText;
  }

  if (group === 'polygenic') {
    return relativeRiskGlance(order) ?? summaryText;
  }

  return summaryText;
};

const summaryGroup = (order: Order, isMedicationResponse: boolean): PatientResultsSummaryGroup => {
  if (isMedicationResponse) {
    return 'medication';
  }

  if (isRareDiseaseProduct(order)) {
    return 'rareDisease';
  }

  const findings = (order.results ?? []).map(result => String(result.finding));
  if (findings.some(finding => RISK_FINDINGS.has(finding))) {
    return 'polygenic';
  }

  return 'panel';
};

const primaryFinding = (order: Order): string | null => {
  const findings = (order.results ?? []).map(result => String(result.finding));
  if (!findings.length) {
    return null;
  }

  const ranked = [
    OrderResultFinding.POSITIVE,
    OrderResultFinding.UNCERTAIN,
    OrderResultFinding.HIGH_RISK,
    OrderResultFinding.INCREASED_RISK,
    OrderResultFinding.ELEVATED_RISK,
    OrderResultFinding.CONTRAINDICATED,
    OrderResultFinding.MAJOR_IMPACT,
    OrderResultFinding.INTERMEDIATE_RISK,
    OrderResultFinding.BORDERLINE_RISK,
    OrderResultFinding.ALTERED_DOSE,
    OrderResultFinding.NEGATIVE,
    OrderResultFinding.NOT_INCREASED_RISK,
    OrderResultFinding.AVERAGE_RISK,
    OrderResultFinding.DECREASED_RISK,
    OrderResultFinding.LOW_RISK,
    OrderResultFinding.MINIMAL_IMPACT,
    OrderResultFinding.NOT_ANALYZED,
  ];

  return ranked.find(finding => findings.includes(finding)) ?? findings[0];
};

const collectReportedOrders = (requisitions: Requisition[]): Order[] => {
  const uniqueOrders = new Map<string, Order>();

  requisitions.forEach(requisition => {
    requisition.orders.forEach(order => {
      if (!isOrderReported(order) || order.product_id === PRODUCT_ID_DATA_ONLY) {
        return;
      }

      const existing = uniqueOrders.get(order.order_id);
      if (!existing || order.created_at > existing.created_at) {
        uniqueOrders.set(order.order_id, order);
      }
    });
  });

  return Array.from(uniqueOrders.values());
};

export const getPatientResultsSummaryRows = (requisitions: Requisition[]): PatientResultsSummaryRow[] => {
  return collectReportedOrders(requisitions)
    .filter(orderHasSummaryData)
    .map(order => {
      const labels = getOrderTestDisplayLabels(order);
      const summaryText = getOrderSummaryText(order)!;
      const isMedicationResponse = isPgxSummaryOrder(order);
      const group = summaryGroup(order, isMedicationResponse);

      return {
        orderId: order.order_id,
        testHeadline: labels.headline,
        testSubline: labels.subline,
        summaryText,
        glanceDetail: glanceDetail(order, group, summaryText),
        resultType: getOrderResult(order),
        finding: isMedicationResponse ? null : primaryFinding(order),
        group,
        isMedicationResponse,
        reportedAt: order.reported_at,
      };
    })
    .sort((a, b) => a.testHeadline.localeCompare(b.testHeadline));
};

export const hasPatientResultsSummary = (requisitions: Requisition[]): boolean =>
  getPatientResultsSummaryRows(requisitions).length > 0;
